/**
 * Where the money goes.
 *
 * Two records sit under this page and they are not the same size. Lobbyists
 * disclose roughly fifty million dollars a year to the whole of Congress,
 * which is less than one competitive Senate seat costs — it shows a
 * relationship, not the weight of one, and the page says so rather than
 * letting the reader assume they are looking at the whole of political
 * money.
 *
 * The other record is much larger per cheque and goes nowhere near a
 * member: inaugural committees and presidential libraries, which have no
 * contribution ceiling at all. One disclosed gift there is fifty million
 * dollars, against a legal limit of a few thousand on anything a member of
 * Congress may accept. It leads, because it is the most striking thing in
 * the corpus and it was invisible while every total was a member total.
 */

import { useSearchParams, Link } from "react-router-dom";
import { OTHER_KINDS, api, type OtherKind } from "../api";
import { AroundThem, BesideCongress } from "../components/AroundThem";
import { OtherGifts } from "../components/OtherGifts";
import { useAsync, useDebounced } from "../useAsync";
import { Problem, Spinner } from "../components/bits";
import { Pager } from "../components/Pager";
import { offsetFor, pageCount } from "../components/paging";
import { brief } from "../components/party";

const PER_PAGE = 40;

const FIRM_SORTS: { key: "money" | "members" | "gifts" | "name"; label: string }[] = [
  { key: "money", label: "Most to members of Congress" },
  { key: "members", label: "Reaches the most members" },
  { key: "gifts", label: "Most separate cheques" },
  { key: "name", label: "Name (A–Z)" },
];

export function Lobbying() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const sort = (params.get("sort") ?? "money") as "money" | "members" | "gifts" | "name";
  const kind = (params.get("kind") ?? "") as OtherKind | "";
  const page = Number(params.get("page") ?? 1);
  const settled = useDebounced(query);

  const other = useAsync(() => api.otherMoney({ kind: kind || undefined, limit: 25 }), [kind]);
  const firms = useAsync(
    () =>
      api.firms({
        q: settled || undefined,
        sort,
        limit: PER_PAGE,
        offset: offsetFor(page, PER_PAGE),
      }),
    [settled, sort, page],
  );

  function set(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    /** Any other change returns to page one. Page 40 of a new search is
     *  empty, which reads as "no results" rather than "wrong page". */
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <h1 className="font-display text-3xl font-semibold">Where the money goes</h1>
      <p className="mt-1.5 max-w-3xl text-muted">
        Twice a year every registered lobbyist has to certify what political money they gave and to
        whom. It is the only public record that puts a named lobbyist, a named recipient and an
        amount on one line — the FEC has the money but not the lobbyist.
      </p>

      {/* The presidential money first. It is the largest thing here and it
          belongs to nobody in Congress, so no member page can show it. */}
      <section className="mt-10">
        <h2 className="font-display text-2xl font-semibold">The money no member receives</h2>
        <p className="mt-1.5 max-w-3xl text-muted">
          A member of Congress may accept a few thousand dollars from any one giver. A presidential
          library or an inaugural committee may accept any amount at all, and the same corporations
          file both. These are the largest disclosed sums in the record.
        </p>

        {other.error && <Problem error={other.error} retry={other.reload} />}
        {other.loading && <Spinner label="Reading the disclosures…" />}
        {other.data && (
          <>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {other.data.summary.map((row) => {
                const on = kind === row.kind;
                return (
                  <button
                    key={row.kind}
                    type="button"
                    onClick={() => set({ kind: on ? "" : row.kind })}
                    aria-pressed={on}
                    className={`rounded-2xl border p-5 text-left shadow-sm transition ${
                      on ? "border-brand bg-sunk" : "border-line bg-card hover:border-brand/50"
                    }`}
                  >
                    <div className="font-display text-2xl font-semibold tabular-nums">
                      {brief(row.total)}
                    </div>
                    <div className="mt-0.5 font-medium">{OTHER_KINDS[row.kind].label}</div>
                    <p className="mt-2 text-sm leading-relaxed text-muted">
                      {OTHER_KINDS[row.kind].blurb}
                    </p>
                    <div className="mt-3 text-xs text-faint">
                      {row.gifts.toLocaleString()} disclosed · {row.donors.toLocaleString()} giving
                      organisations
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Above the list, because a thousand rows is not a finding and
                the two findings in this money are both invisible in one:
                that inaugural giving is a four-yearly pulse, and that a
                category can be a single cheque. */}
            <AroundThem data={other.data} />

            {kind && (
              <p className="mt-4 text-sm text-muted">
                Showing {OTHER_KINDS[kind].label.toLowerCase()} only.{" "}
                <button
                  type="button"
                  onClick={() => set({ kind: "" })}
                  className="text-brand hover:underline"
                >
                  Show all three
                </button>
              </p>
            )}

            {/* The cheques themselves, closed. They were the page for a
                long time -- twenty-five rows of free text a screen -- and
                they are evidence rather than the finding, so they sit one
                click behind the charts that are. */}
            <details className="group mt-8 rounded-2xl border border-line bg-card shadow-sm">
              <summary className="cursor-pointer list-none px-5 py-4 font-medium hover:text-brand">
                <span className="mr-2 inline-block transition group-open:rotate-90">›</span>
                Show the individual cheques
                <span className="ml-2 font-normal text-faint">
                  {other.data.total.toLocaleString()} of them, largest first
                </span>
              </summary>
              <div className="border-t border-line px-5 pb-5">
                <OtherGifts gifts={other.data.gifts} />
                <p className="mt-3 text-sm text-faint">
                  The recipient is shown exactly as the filer typed it, which is why the chart above
                  has to group them. Dates are as filed and approximate — a filer entering the last
                  day of the reporting period rather than the day of the cheque is common enough
                  that 30 June and 31 December are over-represented.
                </p>
              </div>
            </details>

            {/* Last, flush against the section it hands off to. It ends
                "the rest went to members of Congress", and for a long time
                what actually followed it was a thousand rows of
                presidential library cheques. */}
            <BesideCongress data={other.data} />
          </>
        )}
      </section>

      {/* Then Congress, ranked on the money that actually got there. */}
      <section className="mt-16">
        <h2 id="who-funds-congress" className="scroll-mt-20 font-display text-2xl font-semibold">
          Who funds Congress
        </h2>
        <p className="mt-1.5 max-w-3xl text-muted">
          Organisations ranked by what reached a named member. Money a firm filed against a party
          committee or a recipient we could not identify is shown on its page but does not rank it —
          otherwise a single uncapped donation to a president outranks every trade association in
          the country.
        </p>

        {/* The party committee half of that sentence used to be a dead end:
            $141m filed against "DSCC" and no page to send anyone to. */}
        <p className="mt-3 max-w-3xl rounded-xl border border-line bg-card p-4 text-sm text-muted">
          A large share of what a lobbying firm gives goes to the DSCC, NRSC, DCCC or NRCC, none of
          which is a member of Congress —{" "}
          <Link to="/money/committees" className="font-medium text-brand hover:underline">
            follow it to the party committees
          </Link>{" "}
          to see what each was given and what it then spent on candidates by name.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <input
            value={query}
            onChange={(event) => set({ q: event.target.value })}
            type="search"
            aria-label="Search organisations"
            placeholder="Search organisations…"
            className="min-w-64 flex-1 rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm placeholder:text-faint"
          />
          <select
            value={sort}
            onChange={(event) => set({ sort: event.target.value })}
            aria-label="Sort organisations"
            className="rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm"
          >
            {FIRM_SORTS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {firms.error && <Problem error={firms.error} retry={firms.reload} />}
        {firms.loading && <Spinner label="Counting the cheques…" />}
        {firms.data && firms.data.firms.length === 0 && (
          <p className="mt-8 text-muted">Nothing matches “{query}”.</p>
        )}
        {firms.data && firms.data.firms.length > 0 && (
          <>
            {/* The count used to stand alone above forty rows that stopped
                without a word, so it read as a promise the page broke. */}
            <p className="mt-4 text-sm text-muted">
              {firms.data.total.toLocaleString()} organisations have disclosed giving
              {firms.data.total > PER_PAGE && (
                <>
                  {" · showing "}
                  <span className="tnum">
                    {(offsetFor(page, PER_PAGE) + 1).toLocaleString()}–
                    {Math.min(
                      offsetFor(page, PER_PAGE) + firms.data.firms.length,
                      firms.data.total,
                    ).toLocaleString()}
                  </span>
                </>
              )}
              .
            </p>
            <ol className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
              {firms.data.firms.map((firm) => (
                <li key={firm.registrant_id}>
                  <Link
                    to={`/money/${firm.registrant_id}`}
                    className="flex flex-wrap items-baseline gap-x-5 gap-y-1 px-5 py-4 transition hover:bg-sunk"
                  >
                    <span className="w-24 font-display text-lg font-semibold tabular-nums">
                      {brief(firm.to_members)}
                    </span>
                    <span className="min-w-48 flex-1 font-medium">{firm.name}</span>
                    <span className="text-sm text-muted tabular-nums">
                      {firm.members.toLocaleString()} members · {firm.gifts.toLocaleString()} cheques
                    </span>
                    {firm.elsewhere > 0 && (
                      <span className="text-sm text-faint tabular-nums">
                        {brief(firm.elsewhere)} elsewhere
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ol>
            <Pager
              page={page}
              pages={pageCount(firms.data.total, PER_PAGE)}
              onPage={(next) => set({ page: String(next) })}
              label="Organisations"
            />
          </>
        )}
      </section>

      <p className="mt-10 max-w-3xl text-sm text-faint">
        Every total here is a floor. Recipients are free text on the LD-203 and are matched to
        members by name, so a misspelling is money we did not count. Contributions to a member's
        campaign are separate from the cost of an event held in their honour, which the member never
        receives, and the two are never added together.
      </p>
    </div>
  );
}
